import { useCallback, useEffect, useState } from "react";
import { apiUrl, authFetch, authHeaders } from "../config";
import styles from "./FacebookMetricoolSettings.module.css";

interface FacebookAccount {
  id: number;
  name: string;
  facebook_brand_name: string;
  metricool_user_id: string;
  metricool_blog_id: string;
  metricool_key_name: string;
  is_default: number;
}

const keys = ["METRICOOL_API_KEY", "METRICOOL_API_KEY_2", "METRICOOL_API_KEY_3", "METRICOOL_API_KEY_4", "METRICOOL_API_KEY_5"];
const blankForm = { name: "", facebook_brand_name: "", metricool_key_name: "", is_default: false };

export function FacebookMetricoolSettings() {
  const [accounts, setAccounts] = useState<FacebookAccount[]>([]);
  const [form, setForm] = useState(blankForm);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const response = await authFetch(apiUrl("/api/facebook-metricool/accounts"));
      const data = await response.json() as { accounts?: FacebookAccount[]; error?: string };
      if (!response.ok) throw new Error(data.error || "Failed to load accounts");
      setAccounts(data.accounts || []);
    } catch (err) { setError(err instanceof Error ? err.message : "Failed to load accounts"); }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const add = async () => {
    setSaving(true); setError("");
    try {
      const response = await authFetch(apiUrl("/api/facebook-metricool/accounts"), {
        method: "POST", headers: authHeaders(), body: JSON.stringify(form),
      });
      const data = await response.json() as { error?: string; available_brands?: string[] };
      if (!response.ok) {
        const available = data.available_brands?.length ? ` Available brands: ${data.available_brands.join(", ")}` : "";
        throw new Error(`${data.error || "Failed to add account"}${available}`);
      }
      setForm(blankForm); setOpen(false); await load();
    } catch (err) { setError(err instanceof Error ? err.message : "Failed to add account"); }
    finally { setSaving(false); }
  };

  const remove = async (id: number) => {
    if (!confirm("Delete this Facebook publishing account?")) return;
    const response = await authFetch(apiUrl(`/api/facebook-metricool/accounts/${id}`), { method: "DELETE" });
    if (response.ok) await load(); else setError("Failed to delete account");
  };

  const makeDefault = async (id: number) => {
    const response = await authFetch(apiUrl(`/api/facebook-metricool/accounts/${id}`), {
      method: "PATCH", headers: authHeaders(), body: JSON.stringify({ is_default: true }),
    });
    if (response.ok) await load(); else setError("Failed to set default account");
  };

  return (
    <section className={styles.section}>
      <div className={styles.header}>
        <div>
          <span className={styles.eyebrow}>Facebook publishing</span>
          <h2>Metricool Facebook Accounts</h2>
          <p>Use Brand Name and API key. Metricool profile IDs are detected automatically.</p>
        </div>
        <button className={styles.primary} onClick={() => setOpen(v => !v)}>{open ? "Close" : "+ Add Facebook account"}</button>
      </div>

      {error && <div className={styles.error}>{error}</div>}
      {open && (
        <div className={styles.form}>
          <label>Display name<input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Facebook Recipes Page" /></label>
          <label>Brand Name<input value={form.facebook_brand_name} onChange={e => setForm({ ...form, facebook_brand_name: e.target.value })} placeholder="Exact Metricool brand name" /></label>
          <label>API key<select value={form.metricool_key_name} onChange={e => setForm({ ...form, metricool_key_name: e.target.value })}><option value="">Select key…</option>{keys.map(key => <option key={key}>{key}</option>)}</select></label>
          <label className={styles.check}><input type="checkbox" checked={form.is_default} onChange={e => setForm({ ...form, is_default: e.target.checked })} /> Use as default</label>
          <button className={styles.save} disabled={saving} onClick={add}>{saving ? "Saving…" : "Save account"}</button>
        </div>
      )}

      <div className={styles.grid}>
        {accounts.map(account => (
          <article className={styles.card} key={account.id}>
            <div className={styles.cardTop}><span className={styles.icon}>f</span><div><h3>{account.name}</h3><p>{account.facebook_brand_name}</p></div>{account.is_default === 1 && <span className={styles.default}>Default</span>}</div>
            <dl><div><dt>Brand Name</dt><dd>{account.facebook_brand_name}</dd></div><div><dt>API key</dt><dd>{account.metricool_key_name.replace("METRICOOL_API_KEY", "Key") || "—"}</dd></div></dl>
            <div className={styles.actions}>{account.is_default !== 1 && <button onClick={() => makeDefault(account.id)}>Set default</button>}<button className={styles.delete} onClick={() => remove(account.id)}>Delete</button></div>
          </article>
        ))}
        {!accounts.length && <div className={styles.empty}>No Facebook accounts yet. Add the Facebook Page you want to publish Reels to.</div>}
      </div>
    </section>
  );
}
